import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import Configuration from '../config';

const SCRIPT_ID = 'google-identity-services';
let initializedIdentityApi = null;
let initializedClientId = null;
const credentialConsumers = [];

const getIdentityApi = () => {
  if (typeof window === 'undefined') return null;
  const identityApi = window.google && window.google.accounts && window.google.accounts.id;
  if (!identityApi || typeof identityApi.initialize !== 'function' || typeof identityApi.renderButton !== 'function') return null;
  return identityApi;
};

const deliverCredential = response => {
  const consumer = credentialConsumers[credentialConsumers.length - 1];
  if (consumer && response && response.credential) consumer(response.credential);
};

const initializeIdentityApi = identityApi => {
  if (initializedIdentityApi === identityApi && initializedClientId === Configuration.googleClientId) return;
  identityApi.initialize({
    client_id: Configuration.googleClientId,
    callback: deliverCredential,
    ux_mode: 'popup'
  });
  initializedIdentityApi = identityApi;
  initializedClientId = Configuration.googleClientId;
};

const GoogleSignIn = ({onCredential, className}) => {
  const buttonRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const lastRenderedWidthRef = useRef(0);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!Configuration.googleClientId) return undefined;

    const credentialConsumer = credential => onCredentialRef.current(credential);
    credentialConsumers.push(credentialConsumer);
    let animationFrame = null;

    const render = () => {
      animationFrame = null;
      const identityApi = getIdentityApi();
      if (!identityApi || !buttonRef.current) return;
      const renderTarget = buttonRef.current;
      const measuredContainer = renderTarget.parentElement || renderTarget;
      const availableWidth = Math.floor(
        measuredContainer.clientWidth || measuredContainer.getBoundingClientRect().width || renderTarget.getBoundingClientRect().width
      );
      const buttonWidth = availableWidth > 0 ? availableWidth : 320;
      if (lastRenderedWidthRef.current === buttonWidth && renderTarget.childNodes.length > 0) return;
      lastRenderedWidthRef.current = buttonWidth;
      renderTarget.innerHTML = '';
      initializeIdentityApi(identityApi);
      identityApi.renderButton(renderTarget, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: buttonWidth,
        locale: 'it'
      });
    };

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const scheduleRender = () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(render);
    };

    script.addEventListener('load', scheduleRender);
    if (window.google) scheduleRender();

    const resizeObserver = window.ResizeObserver && buttonRef.current && buttonRef.current.parentElement ? new window.ResizeObserver(scheduleRender) : null;
    if (resizeObserver && buttonRef.current.parentElement) resizeObserver.observe(buttonRef.current.parentElement);
    window.addEventListener('resize', scheduleRender);

    return () => {
      script.removeEventListener('load', scheduleRender);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleRender);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      const consumerIndex = credentialConsumers.lastIndexOf(credentialConsumer);
      if (consumerIndex >= 0) credentialConsumers.splice(consumerIndex, 1);
    };
  }, []);

  if (!Configuration.googleClientId) return null;
  return <div ref={buttonRef} className={`login-skywork-google my-3 ${className}`.trim()}/>;
};

GoogleSignIn.propTypes = {
  onCredential: PropTypes.func.isRequired,
  className: PropTypes.string
};
GoogleSignIn.defaultProps = {className: ''};

export default GoogleSignIn;
